/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import * as _ from 'lodash';
import {AppEventBusService} from '../../app/common/events/app-event-bus.service';

import {AUTH_EVENT} from '../auth/auth-constants';
import {AuthenticatedUserResource} from '../api/resources/resource/authenticated-user';
import {ElectionResource} from '../api/resources/resource/election.resource';
import {NetworkInfoResource} from '../api/resources/resource/network.resource';
import {LocalNetworkInfo} from '../session/local-network-info';
import {LocalNetworkProfile} from '../session/local-network-profile';
import {LocalPausing} from '../session/local-pausing';
import {LocalDebugInfo} from '../session/local-debug-info';
import {LocalUserPermissions} from '../session/local-user-permissions';
import {LocalUserProfile} from '../session/local-user-profile';
import {ApiConfig} from '../api/api';
import {UpdateSession} from '../session/update-session';
import {WorkspaceRegistryService} from '../workspace-registry.service';
import {WINDOW} from '../../app/angular-platform-providers';

@Injectable({
  providedIn: 'root'
})
export class AdnAuthHelperService {
  constructor(
    private readonly authenticatedUserResource: AuthenticatedUserResource,
    @Inject(WINDOW) private readonly window: any,
    private readonly toastr: ToastrService,
    @Inject(ElectionResource) private readonly Election: any,
    private readonly LocalUserPermissions: LocalUserPermissions,
    private readonly eventBus: AppEventBusService,
    private readonly WorkspaceRegistry: WorkspaceRegistryService,
    private readonly LocalNetworkProfile: LocalNetworkProfile,
    @Inject(NetworkInfoResource) private readonly NetworkInfo: any,
    private readonly localUserProfile: LocalUserProfile
  ) {
  }

  doAuth(ctrlProfile: any, credentials: any, masqueradeAttempt?: any, networkTag?: any): any {
    UpdateSession.obtain().destroy();
    return this.authenticatedUserResource.get().then((authUser: any) => {
      const user = authUser.user;
      const prevDefaultNetworkTagId = networkTag
        || this.LocalNetworkProfile.get('tagId')
        || this.localUserProfile.get('defaultNetworkTagId');
      const prevApp = this.localUserProfile.get('app');
      const isPrevUser = this.localUserProfile.isSameUser(ctrlProfile);
      const profile = this.localUserProfile.create(
        user,
        _.keys(authUser.appsByNetwork),
        authUser.profile,
        credentials.masqUsername && masqueradeAttempt ? credentials.username : null
      );

      this.LocalUserPermissions.create(authUser);
      LocalPausing.obtain().destroy();

      if (isPrevUser && prevDefaultNetworkTagId && this.localUserProfile.hasNetwork(prevDefaultNetworkTagId)) {
        profile.defaultNetworkTagId = prevDefaultNetworkTagId;
        this.localUserProfile.save();
      } else {
        const networkWithMostTeams = this.LocalUserPermissions.getNetworkWithMostTeams(profile.networkTagIds);
        if (networkWithMostTeams) {
          profile.defaultNetworkTagId = networkWithMostTeams;
          this.localUserProfile.save();
        }
      }

      if (isPrevUser) {
        this.localUserProfile.updateDefaultNetworkId(this.LocalNetworkProfile.get('tagId'));
      }

      const networkProfileRefresh = this.LocalNetworkProfile.create(profile);
      // Wait for profile context, then refresh network info / elections and include
      // those promises in Promise.all — pushing onto allPromises inside .then is too late.
      const networkInfoAndElections = Promise.resolve(networkProfileRefresh).then(() => {
        return Promise.all([
          this.NetworkInfo.get((data: any) => {
            LocalNetworkInfo.obtain().setInfo(data);
          }).$promise,
          this.Election.get({includeTests: true}, (data: any) => {
            LocalNetworkInfo.obtain().setSuppInfo('elections', data.results);
          }).$promise
        ]);
      });

      const availableApps = this.LocalUserPermissions.getApps();
      if (isPrevUser && prevApp && _.includes(availableApps, prevApp)) {
        profile.app = prevApp;
        this.localUserProfile.save();
      } else if (availableApps.length > 1 && _.includes(availableApps, 'ADVERTISING')) {
        profile.app = 'ADVERTISING';
        this.localUserProfile.save();
      }

      this.WorkspaceRegistry.sync();

      LocalDebugInfo.obtain().setSignIn(
        user.id,
        user.username,
        credentials.username,
        masqueradeAttempt ? credentials.masqUsername : null
      );

      return networkInfoAndElections.then(() => {
        if (ApiConfig.obtain().getEnv() === 'production') {
          const roles = _.sortBy(_.uniq(_.map(this.LocalUserPermissions.getAllVisPermissions(), (_v: any, k: any) => {
            return k;
          })));
          this.window.pendo.identify({
            visitor: {
              id: this.localUserProfile.get('userId'),
              email: this.localUserProfile.get('username'),
              full_name: this.localUserProfile.get('surname')
                ? this.localUserProfile.get('name') + this.localUserProfile.get('surname')
                : this.localUserProfile.get('displayName'),
              role: roles,
              role_string: roles.join(','),
              is_admin: roles.indexOf('MANAGE_SYSTEM') > -1
            },
            account: {
              id: this.LocalNetworkProfile.get('tagId'),
              name: this.LocalNetworkProfile.get('networkName'),
              teams: this.LocalUserPermissions.get('allNetworks').length > 3
                ? 'ADMIN'
                : _.uniq(this.LocalUserPermissions.get('allTeams')).join(',')
            }
          });
        }

        this.eventBus.emit(AUTH_EVENT.loginSuccess, profile);

        // do not show a password expiry notification when masquerading its just confusing
        if (!this.localUserProfile.isMasquerading() && profile.passwordExpiresDays && profile.passwordExpiresDays < 30) {
          this.toastr.warning(
            `Your password expires in ${profile.passwordExpiresDays} day${profile.passwordExpiresDays === 1 ? '' : 's'}.<br /><a href="/admin/user-profile" class="toast-link">Change Your Password</a>`,
            null,
            {enableHtml: true, timeOut: 10000}
          );
        }
      });
    });
  }
}
